import React from 'react';
import Sparkline from './Sparkline';
import { money, signed, signedPct, pct, tone, mmdd } from './format';

const Stat = ({ label, value, sub, cls = '' }) => (
  <div className="df-stat">
    <span className="df-muted">{label}</span>
    <b className={cls}>{value}</b>
    {sub && <span className={`df-sub ${cls}`}>{sub}</span>}
  </div>
);

export default function Overview({ data, history }) {
  const s = data.summary;
  const g = data.greeks;
  const m = data.margin;
  const inc = data.monthly_income || [];
  const si = data.strategy_income || { monthly: [], totals: {} };
  const values = (history || []).map(h => h.portfolio_value);

  return (
    <div className="df-overview">
      <div className="df-stats">
        <Stat label="portfolio" value={money(s.portfolio_value)} sub={s.total_value != null ? `total ${money(s.total_value)}` : null} />
        <Stat label="stock p/l" value={signed(s.stock_pl)} sub={signedPct(s.stock_appreciation_pct)} cls={tone(s.stock_pl)} />
        <Stat label="options p/l" value={signed(s.options_pl)} sub={signedPct(s.options_return_pct, 2)} cls={tone(s.options_pl)} />
        <Stat label="cash" value={money(s.cash)} />
      </div>

      {values.length > 1 && <Sparkline values={values} width={600} height={36} />}

      <p className="df-line">
        <span>{s.covered_calls} cc</span><span>{s.csps} csp</span><span>{s.total_options} options</span>
        {s.expiring_soon.length > 0 && (
          <span className="df-warn">
            expiring: {s.expiring_soon.map(e => `${e.underlying} ${e.strike}${e.call_put[0]} (${e.dte}d)`).join(', ')}
          </span>
        )}
      </p>

      <div className="df-grid">
        <article>
          <header>
            <h3>greeks</h3>
            {g.coverage === 0 ? <span className="df-muted">no greeks from this broker</span>
              : g.coverage < 1 ? <span className="df-muted">{pct(g.coverage * 100)} of contracts</span> : null}
          </header>
          <p className="df-line">
            <span>Δ {g.delta >= 0 ? '+' : ''}{Math.round(g.delta).toLocaleString()}</span>
            <span>θ {signed(g.theta)}/d</span>
            <span>ν {signed(g.vega)}</span>
            <span>γ {g.gamma.toFixed(2)}</span>
          </p>
        </article>

        <article>
          <header><h3>margin</h3><span>{money(m.total_margin)}</span></header>
          <ul className="df-kv">
            <li><span>csp cash</span><span>{money(m.csp_cash)}</span></li>
            <li><span>bull put</span><span>{money(m.bps_margin)}</span></li>
            <li><span>bear call</span><span>{money(m.bear_call_margin)}</span></li>
            <li><span>iron condor</span><span>{money(m.iron_condor_margin)}</span></li>
            {m.naked_call_margin > 0 && <li><span>naked call</span><span>{money(m.naked_call_margin)}</span></li>}
            <li className="df-muted"><span>cc collateral</span><span>{money(m.covered_call_collateral)}</span></li>
          </ul>
        </article>

        <article className="df-wide">
          <header><h3>options income</h3><span className="df-muted">ytd</span></header>
          {inc.length === 0 ? <p className="df-muted">no transactions yet</p> : (
            <table className="df-table">
              <thead><tr><th>month</th><th className="df-num">collected</th><th className="df-num">paid</th><th className="df-num">net</th><th className="df-num">trades</th><th className="df-num">arr</th></tr></thead>
              <tbody>
                {inc.map(r => (
                  <tr key={r.month}>
                    <td>{r.label}</td><td className="df-num">{money(r.received)}</td><td className="df-num">{money(r.paid)}</td>
                    <td className={`df-num ${tone(r.net)}`}>{signed(r.net)}</td><td className="df-num">{r.trades}</td>
                    <td className={`df-num ${r.arr >= 20 ? 'up' : r.arr < 0 ? 'down' : ''}`}>{pct(r.arr, 1)}</td>
                  </tr>
                ))}
                <tr className="df-total">
                  <td>total</td>
                  <td className="df-num">{money(inc.reduce((a, r) => a + r.received, 0))}</td>
                  <td className="df-num">{money(inc.reduce((a, r) => a + r.paid, 0))}</td>
                  <td className={`df-num ${tone(inc.reduce((a, r) => a + r.net, 0))}`}>{signed(inc.reduce((a, r) => a + r.net, 0))}</td>
                  <td className="df-num">{inc.reduce((a, r) => a + r.trades, 0)}</td><td />
                </tr>
              </tbody>
            </table>
          )}
        </article>

        {si.monthly.length > 0 && (
          <article className="df-wide">
            <header><h3>by strategy</h3></header>
            <table className="df-table">
              <thead><tr><th>month</th><th className="df-num">cc</th><th className="df-num">csp</th><th className="df-num">bull put</th><th className="df-num">bear call</th><th className="df-num">other</th><th className="df-num">total</th></tr></thead>
              <tbody>
                {si.monthly.map(r => (
                  <tr key={r.month}>
                    <td>{r.label}</td>
                    {['cc', 'csp', 'bps', 'bear_call', 'other', 'total'].map(k => <td key={k} className={`df-num ${tone(r[k])}`}>{signed(r[k])}</td>)}
                  </tr>
                ))}
                <tr className="df-total">
                  <td>total</td>
                  {['cc', 'csp', 'bps', 'bear_call', 'other', 'total'].map(k => <td key={k} className={`df-num ${tone(si.totals[k])}`}>{signed(si.totals[k])}</td>)}
                </tr>
              </tbody>
            </table>
          </article>
        )}
      </div>
      <p className="df-muted df-foot">expiring within 5 dte · leaps &gt; 180 dte · margin: csp = strike×100, spreads = width×100, ic = wider wing · as of {mmdd(data.as_of)}</p>
    </div>
  );
}
